import { useEffect, useRef, useState } from "react";
import { api, API } from "../lib/api";
import { Clock, LogOut as LogOutIcon, Check, Camera, X, Loader2, MapPin, PartyPopper, AlertTriangle, ShieldAlert, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import LoadingState from "@/components/ui/loading-state";
import { formatJam, formatTanggalPanjang, hariIniISO } from "@/lib/tanggal";
import { useSortState, sortRows, SortTh } from "@/components/ui/sort";

export default function MyAttendance() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  // Pilih shift saat absen (brief owner 29 Agu poin 1). Kosong = pakai jadwal
  // efektif karyawan seperti sebelumnya, jadi perilaku lama tidak berubah.
  const [daftarShift, setDaftarShift] = useState([]);
  const [shiftPilihan, setShiftPilihan] = useState("");
  // Istirahat (poin 3) & laporan visit (poin 4).
  const [istirahatBusy, setIstirahatBusy] = useState(false);
  const [visitModal, setVisitModal] = useState(false);

  useEffect(() => {
    api.get("/shifts")
      .then(r => setDaftarShift((r.data || []).filter(x => x.is_active !== false)))
      .catch(() => { /* dropdown kosong; server tetap memakai jadwal efektif */ });
  }, []);

  const catatIstirahat = async (aksi) => {
    setIstirahatBusy(true);
    try {
      const r = await api.post("/attendance/istirahat", { aksi });
      toast.success(aksi === "mulai" ? "Istirahat dimulai" : "Istirahat selesai");
      setData(d => (d ? { ...d, today: { ...(d.today || {}), istirahat: r.data.istirahat } } : d));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal mencatat istirahat");
    } finally { setIstirahatBusy(false); }
  };
  const [camModal, setCamModal] = useState(null); // "in" | "out" | null
  const [stream, setStream] = useState(null);
  const [captured, setCaptured] = useState(null);
  const [coords, setCoords] = useState(null); // { lat, lng, accuracy }
  const [locStatus, setLocStatus] = useState("idle"); // idle | loading | ok | error
  const [distance, setDistance] = useState(null);
  const [stamina, setStamina] = useState("");
  const [staminaNote, setStaminaNote] = useState("");
  // Tanpa ini, `load()` yang gagal berakhir sebagai unhandled rejection: `data`
  // tetap null selamanya sehingga halaman berhenti di spinner tanpa satu pun
  // pesan — persis gejala "aplikasinya rusak" yang dilaporkan akun yang belum
  // ditautkan ke data karyawan (backend menjawab dengan alasan yang jelas,
  // tapi dulu jawaban itu dibuang).
  //
  // DIPISAH jadi dua state, dan ini bukan kerapian belaka: `load()` juga
  // dipanggil dari `submit()` SESUDAH clock-in/out berhasil tercatat. Waktu
  // keduanya masih satu state, satu GET susulan yang gagal sesaat mengganti
  // SELURUH halaman dengan panel "belum bisa dibuka" — padahal absennya sudah
  // masuk. Panel itu tidak punya jalan keluar (`gagalMuat` cuma kosong lagi
  // kalau `load()` berikutnya sukses, sementara tidak ada lagi yang
  // memanggilnya), jadi satu-satunya jalan keluar adalah reload browser manual
  // — dan tebakan pertama karyawan justru "absennya belum masuk, coba lagi".
  const [gagalMuat, setGagalMuat] = useState("");      // MUAT AWAL — boleh menggantikan halaman
  const [gagalSegarkan, setGagalSegarkan] = useState(""); // muat ulang — cukup banner
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  // Urutan bawaan (dari backend) dipertahankan sampai header diklik.
  const { sortKey, sortDir, toggle } = useSortState();

  // `awal` menandai pemanggilan dari useEffect mount. Kegagalan di luar itu
  // TIDAK boleh menghapus data yang sudah tampil di layar.
  const load = ({ awal = false } = {}) => api.get("/my/attendance")
    .then(r => { setData(r.data); setGagalMuat(""); setGagalSegarkan(""); })
    .catch(e => {
      const pesan = e?.response?.data?.detail
        || "Gagal memuat data absensi. Periksa koneksi, lalu coba lagi.";
      // Belum ada apa pun di layar → panel penuh masih satu-satunya cara
      // menyampaikan pesannya. Sudah ada data → pertahankan halamannya.
      if (awal || !data) setGagalMuat(pesan);
      else { setGagalSegarkan(pesan); toast.error(pesan); }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load({ awal: true }); }, []);

  // Watch position — updated distance to office
  useEffect(() => {
    if (!data?.location) return;
    if (!navigator.geolocation) { setLocStatus("error"); return; }
    setLocStatus("loading");
    const id = navigator.geolocation.watchPosition(
      pos => {
        const c = { lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy };
        setCoords(c);
        setLocStatus("ok");
        if (data?.location) {
          const d = haversine(c.lat, c.lng, data.location.lat, data.location.lng);
          setDistance(d);
        }
      },
      () => setLocStatus("error"),
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 15000 }
    );
    return () => navigator.geolocation.clearWatch(id);
  }, [data?.location]);

  const openCam = async (mode) => {
    // Preflight: check location if geofence is enabled
    if (data?.holiday_today) {
      toast.error(`Hari ini libur: ${data.holiday_today.name} — tidak bisa absen`);
      return;
    }
    if (data?.location && (!coords || locStatus !== "ok")) {
      toast.error("Aktifkan izin lokasi (GPS) dulu untuk absen");
      return;
    }
    if (data?.location && distance != null && distance > data.location.radius_m) {
      toast.error(`Anda ${Math.round(distance)}m dari kantor — di luar radius ${data.location.radius_m}m`);
      return;
    }
    setCamModal(mode); setCaptured(null);
    try {
      const s = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user", width: 480, height: 360 } });
      setStream(s);
      setTimeout(() => { if (videoRef.current) videoRef.current.srcObject = s; }, 100);
    } catch (e) {
      // Absen tanpa selfie TIDAK lagi dikirim (brief owner 29 Agu poin 2).
      // Sebelumnya kamera yang gagal langsung meloloskan absen tanpa foto —
      // itu berarti siapa pun yang menolak izin kamera bisa absen dari mana
      // saja. Backend juga menolaknya, jadi ini bukan sekadar gerbang layar.
      toast.error("Kamera tidak tersedia. Selfie wajib untuk absen — "
                  + "aktifkan izin kamera di browser Anda lalu coba lagi.");
      setCamModal(null);
    }
  };
  const closeCam = () => {
    if (stream) stream.getTracks().forEach(t => t.stop());
    setStream(null); setCamModal(null); setCaptured(null);
  };
  // Lebar maksimum foto selfie absensi.
  // Sebelumnya foto diambil pada resolusi penuh kamera HP, lalu disimpan
  // sebagai base64 DI DALAM MongoDB. Untuk 60 karyawan itu sekitar 9 GB per
  // tahun — disk server penuh sebelum dua tahun, dan backup harian
  // memperbesarnya lagi. Dibatasi 640px, ukurannya turun sekitar 80% sementara
  // wajah tetap jelas terlihat untuk verifikasi HR.
  const LEBAR_MAKS = 640;

  const snap = () => {
    const v = videoRef.current, c = canvasRef.current;
    if (!v || !c) return;
    const lebarAsli = v.videoWidth || 480;
    const tinggiAsli = v.videoHeight || 360;
    const skala = Math.min(1, LEBAR_MAKS / lebarAsli);
    c.width = Math.round(lebarAsli * skala);
    c.height = Math.round(tinggiAsli * skala);
    c.getContext("2d").drawImage(v, 0, 0, c.width, c.height);
    setCaptured(c.toDataURL("image/jpeg", 0.6));
  };

  const submit = async (mode, photo) => {
    setBusy(true);
    try {
      const body = {};
      if (photo) body.photo = photo;
      if (coords) { body.lat = coords.lat; body.lng = coords.lng; }
      // Stamina hanya relevan saat clock-in. Tidak diisi = dikirim null (bukan 0),
      // supaya rata-rata di rekap tidak tertarik ke bawah oleh jawaban kosong.
      if (mode === "in") {
        body.stamina_pct = stamina === "" ? null : Number(stamina);
        if (staminaNote.trim()) body.stamina_note = staminaNote.trim();
        // Shift yang DIPILIH karyawan (poin 1). Kosong = server memakai jadwal
        // efektifnya seperti sebelumnya.
        if (shiftPilihan) body.shift_id = shiftPilihan;
      }
      const { data: tercatat } = await api.post(
        mode === "in" ? "/attendance/clock-in" : "/attendance/clock-out", body);
      toast.success(mode === "in" ? "Clock-in tercatat" : "Clock-out tercatat");
      setStamina(""); setStaminaNote("");
      // Jawaban server dipakai LANGSUNG sebagai kebenaran, tidak menunggu GET
      // susulan. Kalau GET itu gagal sesaat, tombolnya tetap harus berubah jadi
      // "Sudah Clock-in" — kalau masih mengajak ditekan, karyawan menekannya
      // lagi untuk absen yang sebenarnya sudah masuk.
      if (tercatat?.id) {
        setData(d => (d ? { ...d, today: { ...(d.today || {}), ...tercatat } } : d));
      }
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
    setBusy(false); closeCam();
  };

  // Gagal MUAT AWAL ditampilkan sebagai halaman penuh, bukan toast: tombol Clock In/Out
  // tidak boleh muncul kalau datanya sendiri tidak bisa diambil — menekannya pasti
  // gagal juga, dan itu melanggar aturan "jangan tampilkan tombol yang ujungnya ditolak".
  // Kegagalan muat ULANG sesudah absen tercatat TIDAK sampai ke sini (lihat `load`);
  // ia cukup jadi banner supaya absen yang sudah masuk tidak ikut hilang dari layar.
  if (gagalMuat) return (
    <div className="p-4 md:p-8 max-w-[1200px]">
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-10 text-center" data-testid="my-attendance-gagal">
        <ShieldAlert className="w-8 h-8 text-slate-300 mx-auto mb-2" />
        <p className="text-slate-700 font-medium">Absensi Saya belum bisa dibuka</p>
        <p className="text-sm text-slate-500 mt-1">{gagalMuat}</p>
        {/* Tanpa tombol ini, satu-satunya jalan keluar dari panel ini adalah
            reload browser manual — tidak ada lagi yang memanggil `load()`. */}
        <button onClick={() => load({ awal: true })} data-testid="my-attendance-coba-lagi"
          className="mt-4 inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-[#059669] hover:bg-[#047857] text-white text-sm font-medium">
          <RefreshCw className="w-4 h-4" /> Coba lagi
        </button>
      </div>
    </div>
  );
  if (!data) return <div className="p-4 md:p-8 max-w-[1200px]"><LoadingState loading data-testid="my-attendance-loading" /></div>;
  const t = data.today;
  const fmtTime = (iso) => iso ? formatJam(iso) : "-";

  const inRadius = data.location ? (distance != null && distance <= data.location.radius_m) : true;
  const isHoliday = !!data.holiday_today;
  const shift = data.shift_today;

  return (
    <div className="p-4 md:p-8 max-w-[1200px]">
      <header className="mb-6">
        <p className="text-xs font-semibold uppercase tracking-wider text-emerald-600 mb-2">Absensi</p>
        <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900">Absensi Saya</h1>
        <p className="mt-2 text-sm text-slate-500">Clock-in dengan selfie + verifikasi lokasi (radius kantor) untuk cegah kecurangan.</p>
      </header>

      {/* Banners */}
      {gagalSegarkan && (
        <div className="mb-4 bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3" data-testid="my-attendance-gagal-segarkan">
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-amber-900">Angka di layar mungkin belum yang terbaru</p>
            {/* Kalimat ini yang menahan karyawan dari menekan Clock In dua kali:
                yang gagal cuma pengambilan datanya, bukan absennya. */}
            <p className="text-xs text-amber-700 mt-0.5">
              Absen Anda tetap tersimpan di server — yang gagal hanya memuat ulang tampilannya. {gagalSegarkan}
            </p>
          </div>
          <button onClick={() => load()} data-testid="my-attendance-segarkan"
            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-xs font-medium flex-shrink-0">
            <RefreshCw className="w-3.5 h-3.5" /> Muat ulang
          </button>
        </div>
      )}
      {isHoliday && (
        <div className="mb-4 bg-violet-50 border border-violet-200 rounded-xl p-4 flex items-center gap-3" data-testid="banner-holiday">
          <PartyPopper className="w-6 h-6 text-violet-600 flex-shrink-0" />
          <div>
            <p className="text-sm font-semibold text-violet-900">Hari ini libur: {data.holiday_today.name}</p>
            <p className="text-xs text-violet-700">Anda tidak perlu absen. Selamat berlibur! 🎉</p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-6">
        {/* Main card */}
        <div className="lg:col-span-2 bg-gradient-to-br from-[#0F172A] to-emerald-900 rounded-xl p-6 md:p-8 text-white shadow-lg">
          <div className="flex items-start justify-between mb-2 flex-wrap gap-2">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-white/60">Hari ini — {formatTanggalPanjang(hariIniISO(), { denganHari: true })}</p>
              {shift ? (
                <p className="text-sm mt-1 text-emerald-300 font-medium">
                  Shift: <span className="text-white font-semibold">{shift.name}</span> · {shift.jam_masuk} - {shift.jam_pulang}
                  {shift.toleransi_menit ? <span className="text-white/60"> (toleransi {shift.toleransi_menit} mnt)</span> : null}
                </p>
              ) : (
                <p className="text-xs mt-1 text-amber-300">⚠ Belum ada shift default. Pakai jam standar 08:00.</p>
              )}
              {/* Pilih shift sendiri (poin 1) — hanya sebelum clock-in; sesudah
                  absen masuk, shift-nya sudah tercatat dan mengubahnya di sini
                  tidak akan mengubah apa pun (menyesatkan). */}
              {!t?.clock_in && daftarShift.length > 0 && (
                <div className="mt-2">
                  <label className="text-[11px] text-white/60 block mb-1">Pilih shift (opsional)</label>
                  <select value={shiftPilihan} onChange={e => setShiftPilihan(e.target.value)}
                    data-testid="absen-pilih-shift"
                    className="h-8 px-2 rounded border border-white/20 bg-white/10 text-white text-xs">
                    <option value="" className="text-slate-900">Pakai jadwal saya</option>
                    {daftarShift.map(sh => (
                      <option key={sh.id} value={sh.id} className="text-slate-900">
                        {sh.name} ({sh.jam_masuk}-{sh.jam_pulang})
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          </div>

          {/* Istirahat (poin 3) & Laporan Visit (poin 4) */}
          {t?.clock_in && !t?.clock_out && (
            <div className="mt-4 flex flex-wrap items-center gap-2" data-testid="absen-aksi-tambahan">
              {(() => {
                const brs = t?.istirahat || [];
                const berjalan = brs.length > 0 && !brs[brs.length - 1]?.selesai;
                return (
                  <button onClick={() => catatIstirahat(berjalan ? "selesai" : "mulai")}
                    disabled={istirahatBusy} data-testid="absen-istirahat"
                    className="h-9 px-3 rounded-lg bg-white/10 hover:bg-white/20 border border-white/20 text-xs font-medium disabled:opacity-50">
                    {berjalan ? "Selesai Istirahat" : "Mulai Istirahat"}
                  </button>
                );
              })()}
              <button onClick={() => setVisitModal(true)} data-testid="absen-visit"
                className="h-9 px-3 rounded-lg bg-white/10 hover:bg-white/20 border border-white/20 text-xs font-medium">
                Laporan Visit
              </button>
              {(t?.istirahat || []).length > 0 && (
                <span className="text-[11px] text-white/60" data-testid="absen-istirahat-riwayat">
                  Istirahat: {(t.istirahat || [])
                    .map(b => `${b.mulai}${b.selesai ? `-${b.selesai}` : " (berjalan)"}`).join(", ")}
                </span>
              )}
            </div>
          )}

          <div className="mt-4 grid grid-cols-2 gap-6">
            <div>
              <p className="text-xs text-white/60">Clock-in</p>
              <p className="font-display text-2xl md:text-3xl font-bold mt-1">{fmtTime(t?.clock_in)}</p>
              {t?.late_minutes > 0 && <p className="text-xs text-amber-300 mt-1">Terlambat {t.late_minutes} menit</p>}
            </div>
            <div>
              <p className="text-xs text-white/60">Clock-out</p>
              <p className="font-display text-2xl md:text-3xl font-bold mt-1">{fmtTime(t?.clock_out)}</p>
            </div>
          </div>

          <div className="mt-6 flex gap-3">
            <button onClick={() => openCam("in")} disabled={busy || !!t?.clock_in || isHoliday || (data.location && !inRadius)} data-testid="btn-clock-in"
              className="flex-1 h-12 rounded-lg bg-emerald-500 hover:bg-emerald-400 disabled:bg-white/20 disabled:cursor-not-allowed text-white font-semibold text-sm transition-colors inline-flex items-center justify-center gap-2">
              <Camera className="w-4 h-4" /> {t?.clock_in ? "Sudah Clock-in" : "Clock In + Selfie"}
            </button>
            <button onClick={() => openCam("out")} disabled={busy || !t?.clock_in || !!t?.clock_out || (data.location && !inRadius)} data-testid="btn-clock-out"
              className="flex-1 h-12 rounded-lg bg-white hover:bg-white/90 disabled:bg-white/20 disabled:cursor-not-allowed text-slate-900 disabled:text-white font-semibold text-sm transition-colors inline-flex items-center justify-center gap-2">
              <LogOutIcon className="w-4 h-4" /> {t?.clock_out ? "Sudah Clock-out" : "Clock Out + Selfie"}
            </button>
          </div>
        </div>

        {/* Location / Distance indicator */}
        <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2 inline-flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> Lokasi Anda</p>
          {!data.location ? (
            <div className="text-xs text-slate-500">Branch Anda belum di-set geofence — bebas absen dari mana saja.</div>
          ) : locStatus === "loading" ? (
            <div className="flex items-center gap-2 text-sm text-slate-500 mt-2"><Loader2 className="w-4 h-4 animate-spin" /> Mengambil GPS…</div>
          ) : locStatus === "error" ? (
            <div className="flex items-start gap-2 text-sm text-rose-700 mt-2 bg-rose-50 border border-rose-200 rounded-lg p-3">
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-semibold">Izin lokasi ditolak</p>
                <p className="text-xs">Aktifkan izin lokasi di browser untuk bisa absen.</p>
              </div>
            </div>
          ) : (
            <>
              <p className="text-xs text-slate-500">Branch: <span className="font-semibold text-slate-900">{data.location.branch}</span></p>
              <p className="text-xs text-slate-500 mt-0.5">Radius izin absen: <span className="font-mono font-semibold">{data.location.radius_m} m</span></p>
              <div className={`mt-3 rounded-lg p-3 border ${inRadius ? "bg-emerald-50 border-emerald-200" : "bg-rose-50 border-rose-200"}`}>
                <p className={`text-xs font-semibold ${inRadius ? "text-emerald-700" : "text-rose-700"}`}>
                  {inRadius ? "✓ Dalam radius" : "✗ Di luar radius"}
                </p>
                <p className={`font-display text-2xl font-bold ${inRadius ? "text-emerald-800" : "text-rose-800"}`}>
                  {distance != null ? `${Math.round(distance)} m` : "…"}
                </p>
                <p className="text-[10px] text-slate-500 mt-1">dari titik kantor</p>
              </div>
              {coords && (
                <p className="text-[10px] text-slate-400 mt-2 font-mono">
                  Anda: {coords.lat.toFixed(5)}, {coords.lng.toFixed(5)} · akurasi ±{Math.round(coords.accuracy || 0)}m
                </p>
              )}
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 mb-6">
        <Stat label="Hadir 30 hari terakhir" value={`${data.stats.days_present_30d} hari`} />
        <Stat label="Total telat 30 hari" value={`${data.stats.total_late_minutes_30d} menit`} tint="rose" />
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-x-auto">
        <div className="px-6 py-4 border-b border-slate-100"><h3 className="font-display font-semibold text-slate-900">Riwayat Absensi</h3></div>
        <table className="w-full text-sm">
          <thead><tr className="bg-slate-50 text-xs font-semibold text-slate-500 uppercase tracking-wider">
            <SortTh label="Tanggal" sortKeyName="date" sortKey={sortKey} sortDir={sortDir} onSort={toggle} testid="my-att-sort-tanggal" />
            <th className="text-left py-3 px-4">Shift</th>
            <SortTh label="Clock-in" sortKeyName="clock_in" sortKey={sortKey} sortDir={sortDir} onSort={toggle} testid="my-att-sort-clockin" />
            <SortTh label="Clock-out" sortKeyName="clock_out" sortKey={sortKey} sortDir={sortDir} onSort={toggle} testid="my-att-sort-clockout" />
            <th className="text-center py-3 px-4">Foto</th>
            <SortTh label="Telat (mnt)" sortKeyName="late_minutes" sortKey={sortKey} sortDir={sortDir} onSort={toggle} align="right" testid="my-att-sort-telat" />
          </tr></thead>
          <tbody>
            {sortRows(data.items, sortKey, sortDir).map(a => (
              <tr key={a.id} className="border-b border-slate-100 hover:bg-slate-50">
                <td className="py-3 px-4 font-medium text-slate-900">{a.date}</td>
                <td className="py-3 px-4 text-xs text-slate-600">{a.shift_name ? `${a.shift_name} (${a.shift_jam_masuk}-${a.shift_jam_pulang})` : "-"}</td>
                <td className="py-3 px-4 font-mono text-slate-700">{fmtTime(a.clock_in)}</td>
                <td className="py-3 px-4 font-mono text-slate-700">{fmtTime(a.clock_out)}</td>
                <td className="py-3 px-4 text-center">
                  {a.has_photo_in && <span className="inline-flex items-center gap-1 text-xs text-emerald-700"><Camera className="w-3 h-3" /> in</span>}
                  {a.has_photo_out && <span className="ml-2 inline-flex items-center gap-1 text-xs text-emerald-700"><Camera className="w-3 h-3" /> out</span>}
                  {!a.has_photo_in && !a.has_photo_out && <span className="text-slate-400 text-xs">-</span>}
                </td>
                <td className="py-3 px-4 text-right">{a.late_minutes > 0 ? <span className="text-rose-700 font-semibold">{a.late_minutes}</span> : <span className="text-emerald-700 inline-flex items-center gap-1"><Check className="w-3 h-3" /> Tepat</span>}</td>
              </tr>
            ))}
            {data.items.length === 0 && <tr><td colSpan={6} className="text-center py-10 text-sm text-slate-500"><Clock className="w-8 h-8 text-slate-300 mx-auto mb-2" />Belum ada riwayat</td></tr>}
          </tbody>
        </table>
      </div>

      {visitModal && <VisitModal onClose={() => setVisitModal(false)} />}

      {camModal && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm grid place-items-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md md:max-w-3xl overflow-hidden">
            <div className="border-b border-slate-200 px-6 py-4 flex items-center justify-between">
              <h3 className="font-display font-semibold text-slate-900">Selfie {camModal === "in" ? "Clock-in" : "Clock-out"}</h3>
              <button onClick={closeCam} className="p-2 hover:bg-slate-100 rounded-lg"><X className="w-4 h-4" /></button>
            </div>
            {/* Kamera di KANAN, isian di KIRI (brief owner 29 Agu, Absensi
                poin 6). Di layar sempit tetap bertumpuk dengan kamera di ATAS —
                di HP, isian yang mendorong kamera keluar layar membuat orang
                menggeser-geser untuk melihat wajahnya sendiri. */}
            <div className="p-6 md:grid md:grid-cols-2 md:gap-6">
              <div className="md:order-2">
                <div className="relative rounded-lg overflow-hidden bg-slate-900 aspect-[4/3]">
                  {!captured ? (
                    <video ref={videoRef} autoPlay playsInline muted className="w-full h-full object-cover" />
                  ) : (
                    <img src={captured} alt="captured" className="w-full h-full object-cover" />
                  )}
                </div>
                <canvas ref={canvasRef} className="hidden" />
              </div>
              <div className="md:order-1">
              {camModal === "in" && (
                <div className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50/60 p-3"
                  data-testid="stamina-block">
                  <div className="flex items-center justify-between mb-2">
                    <label htmlFor="stamina-range" className="text-sm font-medium text-slate-800">
                      Stamina kamu hari ini?
                    </label>
                    <span className="font-display text-lg font-bold text-emerald-700 tabular-nums"
                      data-testid="stamina-value">
                      {stamina === "" ? "—" : `${stamina}%`}
                    </span>
                  </div>
                  <input id="stamina-range" type="range" min={0} max={100} step={5}
                    value={stamina === "" ? 70 : stamina}
                    onChange={e => setStamina(Number(e.target.value))}
                    data-testid="stamina-range"
                    className="w-full accent-emerald-600" />
                  <div className="flex justify-between text-[10px] text-slate-500 mb-2">
                    <span>0% · sangat lelah</span><span>100% · sangat bugar</span>
                  </div>
                  <input value={staminaNote} onChange={e => setStaminaNote(e.target.value)}
                    placeholder="Catatan (opsional) — mis. kurang tidur, sedang kurang enak badan"
                    maxLength={200} data-testid="stamina-note"
                    className="w-full h-9 px-2.5 rounded border border-slate-200 text-xs bg-white" />
                  <p className="text-[10px] text-slate-500 mt-1.5">
                    Boleh dilewati. {stamina === "" && "Geser dulu kalau ingin mengisi."}
                  </p>
                </div>
              )}

              <div className="mt-4 flex gap-2">
                {!captured ? (
                  <button onClick={snap} data-testid="btn-snap"
                    className="flex-1 h-11 rounded-lg bg-[#059669] hover:bg-[#047857] text-white font-medium inline-flex items-center justify-center gap-2">
                    <Camera className="w-4 h-4" /> Ambil Foto
                  </button>
                ) : (
                  <>
                    <button onClick={() => setCaptured(null)} className="h-11 px-4 rounded-lg border border-slate-200 text-sm">Ulangi</button>
                    <button onClick={() => submit(camModal, captured)} disabled={busy} data-testid="btn-submit-selfie"
                      className="flex-1 h-11 rounded-lg bg-[#059669] hover:bg-[#047857] text-white font-medium inline-flex items-center justify-center gap-2">
                      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Konfirmasi
                    </button>
                  </>
                )}
              </div>
              {/* Tombol "Lewati foto" DIHAPUS: selfie wajib (brief poin 2) dan
                  backend menolaknya, jadi tombol itu pasti berujung galat —
                  aturan 3 CLAUDE.md, jangan tampilkan tombol yang ujungnya
                  ditolak server. */}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, tint = "emerald" }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 md:p-5">
      <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">{label}</p>
      <p className={`font-display text-xl md:text-2xl font-bold text-${tint}-700`}>{value}</p>
    </div>
  );
}

function haversine(lat1, lng1, lat2, lng2) {
  const R = 6371000;
  const toRad = (x) => (x * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}


/** Laporan kunjungan kerja dengan foto bertimestamp (brief owner 29 Agu,
 *  Absensi poin 4).
 *
 *  Fotonya WAJIB: laporan visit tanpa bukti tidak bisa dibedakan dari laporan
 *  yang diketik dari rumah. Jam & tanggalnya dicatat SERVER — jam perangkat
 *  bisa diubah siapa saja.
 */
function VisitModal({ onClose }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [stream, setStream] = useState(null);
  const [foto, setFoto] = useState(null);
  const [keterangan, setKeterangan] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let hidup = null;
    navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment", width: 640 } })
      .then(sm => {
        hidup = sm; setStream(sm);
        setTimeout(() => { if (videoRef.current) videoRef.current.srcObject = sm; }, 100);
      })
      .catch(() => toast.error("Kamera tidak tersedia — laporan visit butuh foto"));
    return () => { if (hidup) hidup.getTracks().forEach(t => t.stop()); };
  }, []);

  const ambil = () => {
    const v = videoRef.current, c = canvasRef.current;
    if (!v || !c) return;
    const skala = Math.min(1, 640 / (v.videoWidth || 640));
    c.width = Math.round((v.videoWidth || 640) * skala);
    c.height = Math.round((v.videoHeight || 480) * skala);
    c.getContext("2d").drawImage(v, 0, 0, c.width, c.height);
    setFoto(c.toDataURL("image/jpeg", 0.6));
  };

  const kirim = async () => {
    if (!foto) { toast.error("Ambil foto dulu"); return; }
    setBusy(true);
    try {
      let koordinat = {};
      try {
        const pos = await new Promise((res, rej) =>
          navigator.geolocation.getCurrentPosition(res, rej, { timeout: 8000 }));
        koordinat = { lat: pos.coords.latitude, lng: pos.coords.longitude };
      } catch { /* lokasi opsional — fotonya yang wajib */ }
      await api.post("/attendance/visit", { keterangan, photo: foto, ...koordinat });
      toast.success("Laporan visit tersimpan");
      if (stream) stream.getTracks().forEach(t => t.stop());
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyimpan laporan visit");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 grid place-items-center p-4" data-testid="visit-modal">
      <div className="bg-white rounded-2xl max-w-lg w-full shadow-2xl overflow-hidden">
        <div className="p-4 border-b border-slate-100 flex items-center justify-between">
          <h3 className="font-display font-bold">Laporan Visit</h3>
          <button onClick={() => { if (stream) stream.getTracks().forEach(t => t.stop()); onClose(); }}
            className="text-slate-400 hover:text-slate-700 text-xl leading-none">×</button>
        </div>
        <div className="p-4 space-y-3">
          {foto ? (
            <img src={foto} alt="bukti" className="w-full rounded-lg" data-testid="visit-preview" />
          ) : (
            <video ref={videoRef} autoPlay playsInline muted className="w-full rounded-lg bg-slate-900" />
          )}
          <canvas ref={canvasRef} className="hidden" />
          <textarea value={keterangan} onChange={e => setKeterangan(e.target.value)}
            rows={2} placeholder="Keterangan kunjungan (mis. survei toko, antar unit)"
            data-testid="visit-keterangan"
            className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm" />
          <div className="flex justify-end gap-2">
            {foto
              ? <button onClick={() => setFoto(null)} className="h-9 px-3 rounded-lg border border-slate-200 text-sm">Ulangi</button>
              : <button onClick={ambil} data-testid="visit-ambil-foto"
                        className="h-9 px-3 rounded-lg border border-slate-200 text-sm">Ambil Foto</button>}
            <button onClick={kirim} disabled={busy || !foto} data-testid="visit-kirim"
              className="h-9 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-sm font-medium">
              {busy ? "Menyimpan..." : "Kirim Laporan"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
